import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChargingSession, Device, Page, Station } from '@eon/types';
import { api } from '../client';
import {
  camelizeKeys,
  normalizeDevice,
  normalizePage,
  normalizeSession,
  normalizeStation,
} from '../normalize';
import type { ListParams } from './admin';

export interface OmDashboardStats {
  userCount: number;
  sessionCount: number;
  deviceCount: number;
  totalUsedEnergy: number;
}

export interface OmEnergyChartPoint {
  time: string;
  consumption: number;
}

export interface OmDashboardConnector {
  id: number;
  status: string;
  errorCode?: string;
  powerKW?: number;
}

export interface OmDashboardDevice {
  id: string;
  displayName?: string;
  status: string;
  connectors?: OmDashboardConnector[];
}

export interface OmDashboardStation {
  id: string;
  name: string;
  address?: string;
  devices: OmDashboardDevice[];
}

export interface OmDashboardSession {
  id: string;
  stationName?: string;
  deviceDisplayName?: string;
  totalEnergy?: number;
  userName?: string;
  durationSeconds?: number;
  startTime?: string;
  status: string;
}

export interface OmUser {
  id: string;
  userId: string;
  name?: string;
  email?: string;
  phone?: string | null;
  role: string;
  active: boolean;
  status?: string;
  allowanceKwh?: number | null;
  remainingAllowance?: number | null;
  joinedAt: string;
  joinDate?: string;
  lastChargeAt?: string | null;
  rfid?: string;
  totalConsume?: number;
}

export interface OmInvitation {
  id: string;
  email?: string;
  phone?: string;
  role?: string;
  status: string;
  createdAt?: string;
  expiresAt?: string;
}

export interface AllowanceHistoryEntry {
  id: string;
  deltaKwh: number;
  reason?: string;
  createdAt: string;
  actorId?: string;
}

export interface OmOrganizationProfile {
  id: string;
  name: string;
  description?: string;
  active?: boolean;
  /** Allowed overdraft magnitude in kWh (30 → balances may reach -30). */
  creditLimitKwh?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface OmSessionDetail extends ChargingSession {
  stationName?: string;
  deviceName?: string;
  userName?: string;
  meterValues?: Array<{
    time: string;
    measurand: string;
    value: number;
    unit?: string;
  }>;
}

export interface OmSessionListParams extends ListParams {
  status?: string;
  userId?: string;
  deviceId?: string;
  stationId?: string;
  from?: string;
  to?: string;
}

function unwrapData<T>(raw: unknown): T[] {
  const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  if (Array.isArray(obj.data)) return obj.data.map((r) => camelizeKeys<T>(r));
  return [];
}

export function useOmDashboardStats() {
  return useQuery({
    queryKey: ['om', 'dashboard', 'stats'],
    queryFn: async () => {
      const raw = await api().get<unknown>('/om/dashboard/stats');
      return camelizeKeys<OmDashboardStats>(raw);
    },
    refetchInterval: 60_000,
  });
}

export function useOmDashboardEnergyChart(rangeDays = 30) {
  const end = new Date();
  const start = new Date(end.getTime() - rangeDays * 24 * 60 * 60 * 1000);
  return useQuery({
    queryKey: ['om', 'dashboard', 'energy-chart', rangeDays],
    queryFn: async () => {
      const raw = await api().get<unknown>('/om/dashboard/energy-chart', {
        query: { from: start.toISOString(), to: end.toISOString() },
      });
      return unwrapData<OmEnergyChartPoint>(raw);
    },
    refetchInterval: 60_000,
  });
}

export function useOmDashboardStations() {
  return useQuery({
    queryKey: ['om', 'dashboard', 'stations'],
    queryFn: async () => {
      const raw = await api().get<unknown>('/om/dashboard/stations');
      return unwrapData<OmDashboardStation>(raw);
    },
    refetchInterval: 30_000,
  });
}

export function useOmDashboardSessions() {
  return useQuery({
    queryKey: ['om', 'dashboard', 'sessions'],
    queryFn: async () => {
      const raw = await api().get<unknown>('/om/dashboard/sessions');
      return unwrapData<OmDashboardSession>(raw);
    },
    refetchInterval: 30_000,
  });
}

/** @deprecated Use useOmDashboardStats and related hooks */
export function useMyDashboard() {
  const stats = useOmDashboardStats();
  return {
    ...stats,
    data: stats.data
      ? {
          devices: stats.data.deviceCount,
          stations: 0,
          activeSessions: 0,
          energyKWhToday: stats.data.totalUsedEnergy,
        }
      : undefined,
  };
}

export function useMyDevices(params: ListParams = {}) {
  return useQuery({
    queryKey: ['om', 'devices', params],
    queryFn: async (): Promise<Page<Device>> => {
      const raw = await api().get<unknown>('/om/devices', { query: params });
      return normalizePage<Device>(raw, normalizeDevice);
    },
  });
}

export function useMyStations(params: ListParams = {}) {
  return useQuery({
    queryKey: ['om', 'stations', params],
    queryFn: async (): Promise<Page<Station>> => {
      const raw = await api().get<unknown>('/om/stations', { query: params });
      return normalizePage<Station>(raw, normalizeStation);
    },
  });
}

export function useOmStation(stationId: string) {
  return useQuery({
    queryKey: ['om', 'stations', stationId],
    queryFn: async () => {
      const raw = await api().get<unknown>(`/om/stations/${stationId}`);
      return normalizeStation(raw);
    },
    enabled: Boolean(stationId),
  });
}

export interface OmSessionListItem extends ChargingSession {
  stationName?: string;
  deviceDisplayName?: string;
  userName?: string;
}

export function useMySessions(params: OmSessionListParams & { enabled?: boolean } = {}) {
  const { enabled = true, ...query } = params;
  return useQuery({
    queryKey: ['om', 'sessions', query],
    queryFn: async (): Promise<Page<OmSessionListItem>> => {
      const raw = await api().get<unknown>('/om/sessions', { query });
      return normalizePage<OmSessionListItem>(raw, (row) => {
        const obj = (row && typeof row === 'object' ? row : {}) as Record<string, unknown>;
        const session = normalizeSession(obj);
        return {
          ...session,
          stationName: typeof obj.stationName === 'string' ? obj.stationName : undefined,
          userName: typeof obj.userName === 'string' ? obj.userName : undefined,
          deviceDisplayName:
            typeof obj.deviceDisplayName === 'string' ? obj.deviceDisplayName : undefined,
        };
      });
    },
    enabled,
    refetchInterval: enabled ? 15_000 : false,
  });
}

export function useOmSession(sessionId: string) {
  return useQuery({
    queryKey: ['om', 'sessions', sessionId],
    queryFn: async () => {
      const raw = await api().get<unknown>(`/om/sessions/${sessionId}`);
      const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
      const session = normalizeSession(obj);
      const meterValues = Array.isArray(obj.meterValues)
        ? obj.meterValues.map((v) => camelizeKeys<OmSessionDetail['meterValues'] extends (infer U)[] | undefined ? U : never>(v))
        : [];
      return {
        ...session,
        stationName: typeof obj.stationName === 'string' ? obj.stationName : undefined,
        deviceName: typeof obj.deviceName === 'string' ? obj.deviceName : undefined,
        userName: typeof obj.userName === 'string' ? obj.userName : undefined,
        meterValues,
      } as OmSessionDetail;
    },
    enabled: Boolean(sessionId),
  });
}

export function useMyUsers(params: ListParams = {}) {
  return useQuery({
    queryKey: ['om', 'users', params],
    queryFn: async (): Promise<Page<OmUser>> => {
      const raw = await api().get<unknown>('/om/users', { query: params });
      return normalizePage<OmUser>(raw, (r) => camelizeKeys<OmUser>(r));
    },
  });
}

export function useOmInvitations() {
  return useQuery({
    queryKey: ['om', 'users', 'invitations'],
    queryFn: async () => {
      const raw = await api().get<unknown>('/om/users/invitations');
      return normalizePage<OmInvitation>(raw, (r) => camelizeKeys<OmInvitation>(r));
    },
  });
}

export function useOmOrganizationProfile() {
  return useQuery({
    queryKey: ['om', 'profile'],
    queryFn: async () => camelizeKeys<OmOrganizationProfile>(await api().get('/om/profile')),
  });
}

export function useUpdateOmOrganizationProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name?: string; description?: string; creditLimitKwh?: number }) =>
      api().patch('/om/profile', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['om', 'profile'] }),
  });
}

export function useOmDeviceCheck() {
  return useMutation({
    mutationFn: async (serialNumber: string) => {
      const raw = await api().get<unknown>('/om/devices/check', { query: { serialNumber } });
      return normalizeDevice(raw);
    },
  });
}

export function useOmDeviceOwn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, displayName, description }: { deviceId: string; displayName?: string; description?: string }) =>
      api().patch(`/om/devices/own/${deviceId}`, { displayName, description }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['om', 'devices'] });
      qc.invalidateQueries({ queryKey: ['om', 'dashboard'] });
    },
  });
}

export function useOmDeviceUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, displayName, description }: { deviceId: string; displayName?: string; description?: string }) =>
      api().patch(`/om/devices/${deviceId}`, { displayName, description }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['om', 'devices'] }),
  });
}

export function useOmDeviceDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (deviceId: string) => api().delete(`/om/devices/${deviceId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['om', 'devices'] }),
  });
}

export function useOmCreateStation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) => api().post('/om/stations', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['om', 'stations'] }),
  });
}

export function useOmUpdateStation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stationId, ...input }: { stationId: string } & Record<string, unknown>) =>
      api().patch(`/om/stations/${stationId}`, input),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: ['om', 'stations'] });
      qc.invalidateQueries({ queryKey: ['om', 'stations', v.stationId] });
    },
  });
}

export function useOmDeleteStation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (stationId: string) => api().delete(`/om/stations/${stationId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['om', 'stations'] }),
  });
}

export function useOmLinkStationDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stationId, deviceId }: { stationId: string; deviceId: string }) =>
      api().post(`/om/stations/${stationId}/devices`, { deviceId }),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: ['om', 'stations', v.stationId] });
      qc.invalidateQueries({ queryKey: ['om', 'devices'] });
    },
  });
}

export function useOmUnlinkStationDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stationId, deviceId }: { stationId: string; deviceId: string }) =>
      api().delete(`/om/stations/${stationId}/devices/${deviceId}`),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: ['om', 'stations', v.stationId] });
      qc.invalidateQueries({ queryKey: ['om', 'devices'] });
    },
  });
}

export function useOmInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name?: string; email?: string; phoneNumber?: string; message?: string; role?: string }) =>
      api().post('/om/users/invite', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['om', 'users'] }),
  });
}

export function useOmCancelInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      api().post(`/om/users/invitations/${invitationId}/cancel`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['om', 'users', 'invitations'] }),
  });
}

export function useOmResendInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      api().post(`/om/users/invitations/${invitationId}/resend`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['om', 'users', 'invitations'] }),
  });
}

export function useOmUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...input }: { userId: string; name?: string; email?: string; phoneNumber?: string }) =>
      api().patch(`/om/users/${userId}`, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['om', 'users'] }),
  });
}

export function useOmRemoveUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api().delete(`/om/users/${userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['om', 'users'] }),
  });
}

export function useOmActivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api().post(`/om/users/${userId}/activate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['om', 'users'] }),
  });
}

export function useOmAdjustAllowance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, amount, decrease }: { userId: string; amount: number; decrease?: boolean }) =>
      decrease
        ? api().post(`/om/users/${userId}/allowance/decrease`, { amount })
        : api().post(`/om/users/${userId}/allowance`, { amount }),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: ['om', 'users'] });
      qc.invalidateQueries({ queryKey: ['om', 'users', v.userId, 'allowance-history'] });
    },
  });
}

export function useOmAllowanceHistory(userId: string) {
  return useQuery({
    queryKey: ['om', 'users', userId, 'allowance-history'],
    queryFn: async () => {
      const raw = await api().get<unknown>(`/om/users/${userId}/allowance/history`);
      return unwrapData<AllowanceHistoryEntry>(raw);
    },
    enabled: Boolean(userId),
  });
}

export function useOmUserRfid(userId: string) {
  return useQuery({
    queryKey: ['om', 'users', userId, 'rfid'],
    queryFn: async () => {
      const raw = await api().get<unknown>(`/om/users/${userId}/rfid`);
      const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
      if (typeof obj.value === 'string') return obj.value;
      const tag = obj.tag;
      if (tag && typeof tag === 'object') {
        const tagObj = tag as Record<string, unknown>;
        if (typeof tagObj.value === 'string') return tagObj.value;
      }
      return '';
    },
    enabled: Boolean(userId),
  });
}

export function useOmUpsertUserRfid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, value, description }: { userId: string; value: string; description?: string }) =>
      api().put(`/om/users/${userId}/rfid`, { value, description }),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: ['om', 'users'] });
      qc.invalidateQueries({ queryKey: ['om', 'users', v.userId, 'rfid'] });
    },
  });
}

export function useOmSendPasswordReset() {
  return useMutation({
    mutationFn: (userId: string) => api().post(`/om/users/${userId}/password-reset`),
  });
}

export function useOmTestEmail() {
  return useMutation({
    mutationFn: (userId: string) => api().post(`/om/users/${userId}/test-email`),
  });
}

export function useOmTestSms() {
  return useMutation({
    mutationFn: (userId: string) => api().post(`/om/users/${userId}/test-sms`),
  });
}

export interface UpdateProfileInput {
  name?: string;
  surname?: string;
  phone?: string;
  email?: string;
  language?: string;
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProfileInput) => {
      const { language: _omit, ...patch } = input;
      void _omit;
      return api().patch('/my/profile', patch);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}
